/**
 * Cascading Category Select
 * =========================
 * Multi-level category dropdown that shows subcategories progressively.
 *
 * Features:
 * - First dropdown shows top-level categories only
 * - Selecting a category with children shows a new dropdown for subcategories
 * - Continues for unlimited depth
 * - User can stop at any level (doesn't have to select deepest)
 * - Auto-populates when editing a product with existing category
 */

import { useState, useEffect, useMemo } from 'react'
import { Plus } from 'lucide-react'
import type { Category } from '@/types'

interface CascadingCategorySelectProps {
  categories: Category[]
  value: number | null
  onChange: (categoryId: number | null) => void
  onCreateNew?: () => void
  required?: boolean
  disabled?: boolean
}

export default function CascadingCategorySelect({
  categories,
  value,
  onChange,
  onCreateNew,
  required = false,
  disabled = false,
}: CascadingCategorySelectProps) {
  // Track selected category at each level
  const [selectedPath, setSelectedPath] = useState<(number | null)[]>([null])

  // Build a map of parent_id -> children for efficient lookup
  const childrenMap = useMemo(() => {
    const map = new Map<number | null, Category[]>()

    categories.forEach((cat) => {
      const parentId = cat.parent_id
      if (!map.has(parentId)) {
        map.set(parentId, [])
      }
      map.get(parentId)!.push(cat)
    })

    // Sort children by name
    map.forEach((children) => {
      children.sort((a, b) => a.name.localeCompare(b.name))
    })

    return map
  }, [categories])

  // Determine the effective root: if there's only one top-level category, skip to its children
  const effectiveRoot = useMemo(() => {
    const topLevel = childrenMap.get(null) ?? []
    // If there's exactly one top-level category and it has children, use it as the implicit root
    if (topLevel.length === 1) {
      const singleRoot = topLevel[0]
      const rootChildren = childrenMap.get(singleRoot.id) ?? []
      if (rootChildren.length > 0) {
        return singleRoot.id // Skip this level, start from its children
      }
    }
    return null // Start from true root (null parent)
  }, [childrenMap])

  // Build a map of id -> category for quick lookup
  const categoryMap = useMemo(() => {
    const map = new Map<number, Category>()
    categories.forEach((cat) => map.set(cat.id, cat))
    return map
  }, [categories])

  // Get the path from effective root to a given category (excluding the effective root itself)
  const getCategoryPath = (categoryId: number | null): number[] => {
    if (categoryId === null) return []

    const path: number[] = []
    let currentId: number | null = categoryId

    while (currentId !== null) {
      // Stop if we've reached the effective root (don't include it in the path)
      if (currentId === effectiveRoot) break
      path.unshift(currentId)
      const cat = categoryMap.get(currentId)
      currentId = cat?.parent_id ?? null
    }

    return path
  }

  // Initialize or update the selected path when value changes
  useEffect(() => {
    if (value === null) {
      setSelectedPath([null])
      return
    }

    const path = getCategoryPath(value)
    // Build the selection path with nulls for each level
    const newSelectedPath: (number | null)[] = path.map((id) => id)
    // Add a null at the end if the selected category has children (to show the next dropdown)
    if (path.length > 0) {
      const lastCatId = path[path.length - 1]
      const hasChildren = childrenMap.has(lastCatId) && childrenMap.get(lastCatId)!.length > 0
      if (hasChildren) {
        newSelectedPath.push(null)
      }
    }

    setSelectedPath(newSelectedPath.length > 0 ? newSelectedPath : [null])
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getCategoryPath depends on categoryMap/childrenMap already in deps
  }, [value, categoryMap, childrenMap])

  // Handle selection at a specific level
  const handleSelectAtLevel = (level: number, categoryId: number | null) => {
    // Update the path up to this level
    const newPath = selectedPath.slice(0, level)
    newPath[level] = categoryId

    // If a category was selected, check if it has children
    if (categoryId !== null) {
      const hasChildren = childrenMap.has(categoryId) && childrenMap.get(categoryId)!.length > 0
      if (hasChildren) {
        // Add a null entry for the next level dropdown
        newPath.push(null)
      }
    }

    setSelectedPath(newPath)

    // The final selected category is the last non-null value
    const finalCategoryId = categoryId !== null ? categoryId :
      (level > 0 ? newPath[level - 1] : null)
    onChange(finalCategoryId)
  }

  // Get categories available at each level
  const getLevelCategories = (level: number): Category[] => {
    if (level === 0) {
      // Start from effective root's children (or true root if no effective root)
      return childrenMap.get(effectiveRoot) ?? []
    }

    // Get children of the selected category at the previous level
    const parentId = selectedPath[level - 1]
    if (parentId === null) return []

    return childrenMap.get(parentId) ?? []
  }

  // Determine how many dropdowns to show
  const dropdownCount = selectedPath.length

  // Check if current selection is valid (has a non-null value)
  const hasValidSelection = selectedPath.some((id) => id !== null)

  return (
    <div className="space-y-2">
      {Array.from({ length: dropdownCount }).map((_, level) => {
        const levelCategories = getLevelCategories(level)

        // Don't render empty dropdowns (except the first one)
        if (level > 0 && levelCategories.length === 0) return null

        const selectedValue = selectedPath[level]
        const parentName = level > 0 && selectedPath[level - 1] !== null
          ? categoryMap.get(selectedPath[level - 1]!)?.name
          : null

        return (
          <div key={level} className="flex gap-2">
            <select
              value={selectedValue ?? ''}
              onChange={(e) => handleSelectAtLevel(level, e.target.value ? parseInt(e.target.value) : null)}
              disabled={disabled}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white ${
                required && !hasValidSelection && level === 0
                  ? 'border-red-300 dark:border-red-600'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <option value="">
                {level === 0
                  ? 'Select a category'
                  : parentName
                    ? `Select subcategory of ${parentName}...`
                    : 'Select subcategory...'
                }
              </option>
              {levelCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {/* Show create button only on the first dropdown */}
            {level === 0 && onCreateNew && (
              <button
                type="button"
                onClick={onCreateNew}
                disabled={disabled}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 disabled:opacity-50"
                title="Create new category"
              >
                <Plus size={16} />
              </button>
            )}
          </div>
        )
      })}

      {/* Show current selection path as breadcrumb */}
      {hasValidSelection && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Selected: {(() => {
            const selectedIds = selectedPath.filter((id): id is number => id !== null)
            // Include effective root in breadcrumb for full context
            const fullPath = effectiveRoot !== null
              ? [effectiveRoot, ...selectedIds]
              : selectedIds
            return fullPath.map((id) => categoryMap.get(id)?.name).join(' → ')
          })()}
        </div>
      )}
    </div>
  )
}
